/* Based on https://gitlab.com/avandesa/geojson-antimeridian-cut/-/blob/master/src/geometry/lines.js */
import { crossingPoints, antimeridianIntersect } from './util';

/**
 * Position
 *
 * https://tools.ietf.org/html/rfc7946#section-3.1.1
 * Array should contain between two and three elements.
 * The previous GeoJSON specification allowed more elements (e.g., which could be used to represent M values),
 * but the current specification only allows X, Y, and (optionally) Z to be defined.
 */
export declare type Position = number[];

/**
 * LineString Geometry Object
 *
 * https://tools.ietf.org/html/rfc7946#section-3.1.4
 */
export interface LineString {
  type: 'LineString';
  coordinates: Position[];
}

/**
 * A LineString GeoJSON Object
 * @typedef {Object} LineString
 * @property {string} type
 * @property {number[][]} coordinates
 */

/**
 * Splits up an array of coordinates into an array of arrays of coordinates. The fundamental
 * function for breaking up (Multi)?LineStrings
 *
 * @param {number[][]} coordinates The list of coordinates to try to split
 * @param {number[]} crossings The indicies of the `points` that occur immediately at a crossing
 * @returns {number[][][]}
 */
const splitCoordinateArray = (coordinates: number[][], crossings: number[]) => {
  const result = [];

  // Split up into segments on each side of meridian
  result.push(coordinates.slice(0, crossings[0]));
  for (let i = 1; i < crossings.length; i += 1) {
    result.push(coordinates.slice(crossings[i - 1], crossings[i]));
  }
  result.push(coordinates.slice(crossings[crossings.length - 1]));

  // Add in the points on the meridian itself
  for (let i = 1; i < result.length; i += 1) {
    const left = result[i - 1];
    const lastLeft = left[left.length - 1];
    const right = result[i];
    const firstRight = right[0];

    const intersect = antimeridianIntersect(lastLeft, firstRight);

    left.push([180 * Math.sign(lastLeft[0]), intersect]);
    right.unshift([180 * Math.sign(firstRight[0]), intersect]);
  }
  let xOffset = 0;
  for (let i = 1; i < result.length; i += 1) {
    const right = result[i];
    const firstRight = right[0];
    xOffset += -Math.sign(firstRight[0]) * 360;
    for (const pos of right) {
      pos[0] += xOffset;
    }
  }

  return result;
};

/**
 * Examines the `coordinates` of `lineString` to see if it would cross the antimeridian, and if so,
 * transforms it into an equivalent `LineString[]` broken up over the antimeridian.
 *
 * @param {LineString} lineString A `LineString` GeoJSON object to break up
 * @returns {LineString[]}
 */
export const splitLineString = (lineString: LineString): LineString[] => {
  const { coordinates } = lineString;

  const crossings = crossingPoints(coordinates);

  if (crossings.length === 0) {
    return [lineString];
  }

  return splitCoordinateArray(coordinates, crossings).map((coordinates) => ({
    type: 'LineString',
    coordinates,
  }));
};
